import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { authTask, AuthTaskResult } from "src/appStore";
import { langHdr } from "src/lang/langHdr";
import { registerPageStore } from "src/pages/RegisterPage/RegisterPageStore";
import { coalesce } from "./coalesce";

const shell = async <R extends AuthTaskResult>(
  task: () => Promise<R>,
): Promise<R> => (registerPageStore.settingsId ? task() : authTask(task));

const normalizeValue = (value: unknown, seen = new WeakSet()): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeValue(item, seen));
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "function") {
    return value.toString();
  }

  if (value && typeof value === "object") {
    if (seen.has(value)) {
      return "[Circular]";
    }
    seen.add(value);
    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalizeValue(
          (value as Record<string, unknown>)[key],
          seen,
        );
        return acc;
      }, {});
  }

  return value;
};

const makeGetKey = (url: string, config?: AxiosRequestConfig): string =>
  JSON.stringify({
    url,
    config: normalizeValue({
      baseURL: config?.baseURL,
      headers: config?.headers,
      params: config?.params,
      paramsSerializer: config?.paramsSerializer,
      responseType: config?.responseType,
      timeout: config?.timeout,
      withCredentials: config?.withCredentials,
    }),
  });

/**
 * Конфиг запроса: всё, что понимает axios, плюс поля нашего транспорта.
 *
 * Сейчас такое поле одно - translate. До axios оно не доходит: здесь из него
 * собирается заголовок Content-Language, а в restWithObjectVersion - ещё и
 * часть ключа кэша. Приклеивать langHdr() руками не нужно: сделать это, забыв
 * про ключ, - ровно тот случай, из-за которого Управление получало переведённые
 * ответы PLM прямо из HTTP-кэша, не доходя до бэкенда.
 */
export type RequestConfig<D = unknown> = AxiosRequestConfig<D> & {
  translate?: boolean;
};

const toAxios = <D>(
  config?: RequestConfig<D>,
): AxiosRequestConfig<D> | undefined => {
  if (!config) return undefined;
  const { translate, ...axiosConfig } = config;
  if (!translate) return axiosConfig;
  return { ...axiosConfig, headers: { ...axiosConfig.headers, ...langHdr() } };
};

export const rest = {
  get<T = unknown, R extends AuthTaskResult = AxiosResponse<T>, D = unknown>(
    url: string,
    config?: RequestConfig<D>,
  ): Promise<R> {
    // Ключ склейки считается по разрешённому конфигу: иначе переведённый и
    // базовый запрос выглядели бы одинаковыми и склеились бы в один
    const axiosConfig = toAxios(config);
    return coalesce(makeGetKey(url, axiosConfig), () =>
      shell(() => axios.get(url, axiosConfig)),
    ) as Promise<R>;
  },
  post<T = unknown, R extends AuthTaskResult = AxiosResponse<T>, D = unknown>(
    url: string,
    data?: D,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return shell(() => axios.post(url, data, toAxios(config)));
  },
  put<T = unknown, R extends AuthTaskResult = AxiosResponse<T>, D = unknown>(
    url: string,
    data?: D,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return shell(() => axios.put(url, data, toAxios(config)));
  },
  delete<T = void, R extends AuthTaskResult = AxiosResponse<T>, D = unknown>(
    url: string,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return shell(() => axios.delete(url, toAxios(config)));
  },
};

export const stdParamsSerializer = {
  indexes: null,
};
